import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Line } from 'react-chartjs-2';
import { ChartData, ChartOptions } from 'chart.js';
import { trackEvent } from 'utils/analytics';
import { UrlUpdaterFunctions } from 'hooks/useFilters';
import { propertyInsightsClient } from 'clients';
import { HTTPError } from 'clients/HTTPClient';
import { SpotInterface } from 'types';
import { TenantInsightsPeriod, TenantUsage, TenantUsageMonthly } from 'types/PropertyInsights';
import lineGraphImg from 'assets/lineGraph.png';
import { useToast } from 'contexts/ToastContext';
import WidgetBox from 'components/widgets/WidgetBox';
import EmptyWidget from 'components/EmptyWidget/EmptyWidget';
import WidgetErrorMessage from 'components/WidgetErrorMessage/WidgetErrorMessage';
import RectangleSkeleton from 'components/Skeletons/RectangleSkeleton';
import LineChartSkeleton from 'components/Skeletons/LineChartSkeleton';
import AGSimpleSelect from 'components/AGSelect/AGSimpleSelect/AGSimpleSelect';
import AGTAbleWrapper from 'components/AGTable/AGTableWrapper';
import Arrow from 'components/Icons/ArrowIcon';
import MinusIcon from 'components/Icons/MinusIcon';
import { formatCount, getTenantUsageGraphData, tenantInsightsPeriodOptions } from '../helpers';
import styles from './TenantUsageWidget.module.css';

type TenantUsageWidgetProps = {
    spot: SpotInterface;
    filters: {
        tenantUsagePeriod: TenantInsightsPeriod;
        selectedTenants: string;
    };
    urlUpdater: UrlUpdaterFunctions<{
        tenantUsagePeriod: TenantInsightsPeriod;
        selectedTenants: string;
    }>;
};

const chartOptions: ChartOptions<'line'> = {
    elements: {
        point: {
            backgroundColor: 'transparent',
            borderWidth: 0,
            hoverBorderWidth: 0,
        },
        line: { borderWidth: 1 },
    },
    scales: {
        y: {
            grid: { display: true },
            ticks: { maxTicksLimit: 8 },
            title: {
                display: true,
                text: 'Usage',
            },
        },
    },
    interaction: {
        mode: 'index',
        intersect: false,
    },
    plugins: {
        legend: {
            display: true,
            position: 'bottom',
            labels: {
                boxHeight: 8,
                boxWidth: 8,
                padding: 20,
            },
        },
    },
};

const MAX_SELECTED_TENANTS = 10;

function TenantUsageWidget({ spot, filters, urlUpdater }: TenantUsageWidgetProps): JSX.Element {
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [tenantUsageReport, setTenantUsageReport] = useState<TenantUsage[] | null>(null);
    const [tenantUsageMonthlyReport, setTenantUsageMonthlyReport] = useState<TenantUsageMonthly[] | null>(null);

    const { addToast } = useToast();

    const selectedTenants = useMemo(() => {
        return filters.selectedTenants ? filters.selectedTenants.split(',') : [];
    }, [filters.selectedTenants]);

    const { graphData, options }: { graphData: ChartData<'line'> | null; options: ChartOptions<'line'> } =
        useMemo(() => {
            const options = { ...chartOptions };
            if (!selectedTenants.length || !tenantUsageMonthlyReport?.length) return { graphData: null, options };

            const { graphData, hasUsage } = getTenantUsageGraphData(selectedTenants, tenantUsageMonthlyReport);
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            options.scales!.y!.title!.text = hasUsage ? 'Usage' : 'Active Drivers';
            return {
                graphData,
                options,
            };
        }, [selectedTenants, tenantUsageMonthlyReport]);

    const getTenantUsage = useCallback(async () => {
        setLoading(true);
        setErrorMessage('');
        try {
            const [tenantUsage, tenantUsageMonthly] = await Promise.all([
                propertyInsightsClient.getTenantUsage(spot.uuid, filters.tenantUsagePeriod),
                propertyInsightsClient.getTenantUsageMonthly(spot.uuid, filters.tenantUsagePeriod),
            ]);
            const sortedTenantUsage = tenantUsage.sort((a, b) => b.spots - a.spots);
            setTenantUsageReport(sortedTenantUsage);
            setTenantUsageMonthlyReport(tenantUsageMonthly);

            if (tenantUsage && tenantUsage.length > 0 && !filters.selectedTenants) {
                urlUpdater.selectedTenants(
                    sortedTenantUsage
                        .slice(0, 5)
                        .map((t) => t.tenant_id)
                        .join(',')
                );
            }
        } catch (e) {
            if (HTTPError.isHTTPError(e)) {
                setErrorMessage(e.message);
            } else {
                setErrorMessage(e?.toString() || 'Something went wrong');
            }
        } finally {
            setLoading(false);
        }
    }, [spot.uuid, filters.tenantUsagePeriod, filters.selectedTenants]);

    useEffect(() => {
        getTenantUsage();
    }, [spot.uuid, getTenantUsage, filters.tenantUsagePeriod]);

    function handleTenantUsagePeriodSelection(selection: string) {
        urlUpdater.tenantUsagePeriod(selection);
        trackEvent('Changed Tenant Usage Timeframe', {
            spot: spot.pk,
            spotName: spot.name,
            timeframe: selection,
        });
    }

    function handleTenantSelection(tenantId: string) {
        const wasSelected = selectedTenants.includes(tenantId);
        const newSelectedTenants = wasSelected
            ? selectedTenants.filter((t) => t !== tenantId)
            : [...selectedTenants, tenantId];
        if (newSelectedTenants.length > MAX_SELECTED_TENANTS) {
            addToast({
                type: 'error',
                dismissible: true,
                timeout: 3000,
                message: `You can only select up to ${MAX_SELECTED_TENANTS} tenants`,
            });
            return;
        }
        if (!newSelectedTenants.length) {
            addToast({
                type: 'error',
                dismissible: true,
                timeout: 3000,
                message: 'You must have at least 1 tenant selected',
            });
            return;
        }
        urlUpdater.selectedTenants(newSelectedTenants.join(','));
        trackEvent('Changed Tenant Usage Selection', {
            spot: spot.pk,
            spotName: spot.name,
            tenants: newSelectedTenants.join(','),
        });
    }

    const isLoading = loading || !tenantUsageReport || !tenantUsageMonthlyReport || !graphData;

    const [isEmpty, hasUsageForTable] = useMemo(() => {
        const isEmpty = tenantUsageReport && tenantUsageReport.length === 0;
        const hasUsage = !isEmpty && tenantUsageReport && tenantUsageReport.some((t) => t.usage > 0);
        return [isEmpty, hasUsage];
    }, [tenantUsageReport]);

    const dataColumnCount = hasUsageForTable ? 4 : 3;

    return (
        <WidgetBox
            title="Tenant Usage"
            rightElement={
                <AGSimpleSelect
                    options={tenantInsightsPeriodOptions}
                    selected={filters.tenantUsagePeriod}
                    onSelect={handleTenantUsagePeriodSelection}
                    width="160px"
                />
            }
        >
            {errorMessage ? (
                <WidgetErrorMessage message={errorMessage} onRetry={getTenantUsage} />
            ) : isLoading ? (
                <div className={styles.tenantUsageContainer}>
                    <RectangleSkeleton width="200px" height="20px" />
                    <LineChartSkeleton />
                </div>
            ) : isEmpty ? (
                <EmptyWidget imgUrl={lineGraphImg} />
            ) : (
                <div className={styles.tenantUsageContainer}>
                    <Line data={graphData} options={options} style={{ maxHeight: '260px' }} />
                    <AGTAbleWrapper style={{ '--data-col-count': dataColumnCount } as React.CSSProperties}>
                        <thead>
                            <tr>
                                <th>Tenant</th>
                                <th>Spaces</th>
                                <th>Registered Drivers</th>
                                {hasUsageForTable && <th>Usage</th>}
                                <th>Trend</th>
                            </tr>
                        </thead>
                        <tbody>
                            {tenantUsageReport
                                ?.filter((tenant) => Boolean(tenant.tenant_name))
                                .map((tenant) => {
                                    const { tenant_name, spots, registered_drivers, trend_pct, tenant_id, usage } =
                                        tenant;
                                    const trendCellClassName =
                                        trend_pct === 0
                                            ? styles.ceroTrendCell
                                            : trend_pct > 0
                                            ? styles.upTrendCell
                                            : styles.downTrendCell;
                                    return (
                                        <tr key={tenant_id}>
                                            <td>
                                                <input
                                                    type="checkbox"
                                                    checked={selectedTenants.includes(tenant_id)}
                                                    onChange={() => handleTenantSelection(tenant_id)}
                                                />
                                                {tenant_name}
                                            </td>
                                            <td>{formatCount(spots)}</td>
                                            <td>{formatCount(registered_drivers)}</td>
                                            {hasUsageForTable && <td>{formatCount(usage)}</td>}
                                            <td className={`${styles.trendCell} ${trendCellClassName}`}>
                                                {trend_pct === 0 ? (
                                                    <MinusIcon />
                                                ) : (
                                                    <Arrow direction={trend_pct > 0 ? 'up-right' : 'down-right'} />
                                                )}
                                                {trend_pct > 0 && '+'}
                                                {trend_pct}%
                                            </td>
                                        </tr>
                                    );
                                })}
                        </tbody>
                    </AGTAbleWrapper>
                </div>
            )}
        </WidgetBox>
    );
}

export default TenantUsageWidget;
